import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TrendingUp, TrendingDown, DollarSign, Clock, Calendar,
  ChevronRight, AlertCircle, ArrowUpRight, ArrowDownRight, Wallet,
} from "lucide-react";
import { brl } from "../lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authApi = (method, path, body) => {
  const token = localStorage.getItem("cps_token");
  return axios({ method, url: `${API}${path}`, data: body, headers: token ? { Authorization: `Bearer ${token}` } : {} }).then(r => r.data);
};

const monthBR = (key) => {
  if (!key) return "";
  const [y, m] = key.split("-");
  const meses = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${meses[parseInt(m, 10) - 1]}/${y.slice(2)}`;
};

const margemColor = (pct) => {
  if (pct >= 30) return "var(--verde-2)";
  if (pct >= 15) return "var(--gold)";
  if (pct >= 0) return "#d4ac5a";
  return "#c47a7a";
};

export function CashflowPage() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("resumo");
  const navigate = useNavigate();

  const load = useCallback(() => {
    authApi("get", "/financeiro/cashflow").then(setData).catch(e => console.error(e));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="container" style={{ paddingTop: 80 }}><p className="page-sub">Carregando fluxo de caixa…</p></div>;

  const maxMonthValue = Math.max(...data.next_months.map(m => Math.max(m.recebido + m.projecao_receber, m.custos))) || 1;

  return (
    <div className="container">
      <div className="page-head fade-up">
        <div>
          <div className="eyebrow"><Wallet size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "var(--gold)" }} /> Visão consolidada</div>
          <h1 className="page-title">Fluxo de <em>Caixa</em></h1>
          <p className="page-sub">Todas as obras juntas — recebimentos, custos, margem e projeção.</p>
        </div>
      </div>

      {/* Hero KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 24 }}>
        <div className="panel" style={{ padding: 22, border: "1px solid var(--gold-3)", background: "linear-gradient(135deg, rgba(184,147,90,0.08) 0%, transparent 100%)" }} data-testid="cashflow-kpi-recebido">
          <div className="eyebrow" style={{ marginBottom: 10 }}><ArrowDownRight size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "var(--verde-2)" }} />Recebido</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 30, fontStyle: "italic", color: "var(--verde-2)" }}>{brl(data.total_recebido)}</div>
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 6 }}>de {brl(data.total_valor_contratado)} contratados</div>
        </div>
        <div className="panel" style={{ padding: 22 }} data-testid="cashflow-kpi-areceber">
          <div className="eyebrow" style={{ marginBottom: 10 }}><Clock size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "var(--gold)" }} />A receber</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 30, fontStyle: "italic", color: "var(--gold)" }}>{brl(data.total_a_receber_contratual)}</div>
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 6 }}>{brl(data.total_a_receber_pendente)} em parcelas pendentes</div>
        </div>
        <div className="panel" style={{ padding: 22 }} data-testid="cashflow-kpi-custos">
          <div className="eyebrow" style={{ marginBottom: 10 }}><ArrowUpRight size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "#c47a7a" }} />Custos</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 30, fontStyle: "italic", color: "#c47a7a" }}>{brl(data.total_custos)}</div>
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 6 }}>{data.custos_por_categoria.length} categorias</div>
        </div>
        <div className="panel" style={{ padding: 22, border: `1px solid ${margemColor(data.margem_pct)}40` }} data-testid="cashflow-kpi-margem">
          <div className="eyebrow" style={{ marginBottom: 10 }}><TrendingUp size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: margemColor(data.margem_pct) }} />Margem consolidada</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 30, fontStyle: "italic", color: margemColor(data.margem_pct) }}>{brl(data.margem_consolidada)}</div>
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 6 }}>
            <strong style={{ color: margemColor(data.margem_pct) }}>{data.margem_pct}%</strong> sobre recebido
          </div>
        </div>
      </div>

      {/* Projeção 60/90 */}
      <div className="panel" style={{ padding: 22, marginBottom: 24, border: "1px solid var(--gold-3)" }} data-testid="cashflow-projecao">
        <div className="eyebrow" style={{ marginBottom: 14 }}><Calendar size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "var(--gold)" }} />Projeção de entradas</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 18 }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".15em", textTransform: "uppercase" }}>Próximos 60 dias</div>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 36, fontStyle: "italic", color: "var(--verde-2)", marginTop: 4 }} data-testid="proj-60d">{brl(data.projecao_60d)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".15em", textTransform: "uppercase" }}>Próximos 90 dias</div>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 36, fontStyle: "italic", color: "var(--gold)", marginTop: 4 }} data-testid="proj-90d">{brl(data.projecao_90d)}</div>
          </div>
        </div>
        {data.projecao_60d === 0 && data.projecao_90d === 0 && (
          <div style={{ marginTop: 14, padding: "10px 14px", background: "var(--bg-3)", fontSize: 12, color: "var(--ink-3)", fontStyle: "italic", borderLeft: "2px solid var(--gold)" }}>
            <AlertCircle size={13} style={{ display: "inline", marginRight: 8, verticalAlign: "middle", color: "var(--gold)" }} />
            Nenhuma parcela com data de vencimento futura. Cadastre as parcelas pendentes em cada obra (Financeiro → Lançamento de receita com data futura e <strong>pago=false</strong>) para alimentar a projeção.
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18, borderBottom: "1px solid var(--line)" }}>
        {[
          ["resumo", "Resumo mensal"],
          ["obras", "Por obra"],
          ["categorias", "Por categoria"],
          ["pendentes", "Parcelas pendentes"],
        ].map(([k, lbl]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`tab-${k}`}
            style={{
              background: "transparent",
              border: "none",
              padding: "10px 16px",
              cursor: "pointer",
              color: tab === k ? "var(--gold)" : "var(--ink-3)",
              borderBottom: tab === k ? "2px solid var(--gold)" : "2px solid transparent",
              fontSize: 13,
              letterSpacing: ".1em",
              textTransform: "uppercase",
              fontFamily: "inherit",
            }}
          >
            {lbl}
          </button>
        ))}
      </div>

      {tab === "resumo" && (
        <div className="panel" style={{ padding: 22 }} data-testid="resumo-mensal">
          <div className="eyebrow" style={{ marginBottom: 14 }}>Próximos 6 meses</div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", height: 220, marginBottom: 18 }}>
            {data.next_months.map(m => {
              const recH = (m.recebido / maxMonthValue) * 180;
              const projH = (m.projecao_receber / maxMonthValue) * 180;
              const cusH = (m.custos / maxMonthValue) * 180;
              return (
                <div key={m.month} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                  <div style={{ flex: 1, width: "100%", display: "flex", alignItems: "flex-end", gap: 3, justifyContent: "center", minHeight: 180 }}>
                    {(recH > 0 || projH > 0) && (
                      <div style={{ flex: 1, display: "flex", flexDirection: "column-reverse", maxWidth: 22 }}>
                        {recH > 0 && <div style={{ height: recH, background: "var(--verde-2)" }} title={`Recebido: ${brl(m.recebido)}`} />}
                        {projH > 0 && <div style={{ height: projH, background: "var(--gold)", opacity: 0.7, borderTop: recH > 0 ? "1px dashed var(--bg-1)" : "none" }} title={`Projeção: ${brl(m.projecao_receber)}`} />}
                      </div>
                    )}
                    {cusH > 0 && (
                      <div style={{ width: 22, height: cusH, background: "#c47a7a", opacity: 0.85 }} title={`Custos: ${brl(m.custos)}`} />
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: ".1em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{monthBR(m.month)}</div>
                  <div style={{ fontSize: 11, fontFamily: "'Fraunces',serif", fontStyle: "italic", color: m.saldo_estimado >= 0 ? "var(--verde-2)" : "#c47a7a" }}>
                    {m.saldo_estimado >= 0 ? "+" : ""}{Math.round(m.saldo_estimado / 1000)}k
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 18, fontSize: 11, justifyContent: "center", flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, background: "var(--verde-2)", display: "inline-block" }} />Recebido</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, background: "var(--gold)", opacity: 0.7, display: "inline-block" }} />Projeção</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, background: "#c47a7a", opacity: 0.85, display: "inline-block" }} />Custos</span>
          </div>
          <table className="tbl" style={{ marginTop: 22 }}>
            <thead>
              <tr><th>Mês</th><th style={{ textAlign: "right" }}>Recebido</th><th style={{ textAlign: "right" }}>Projeção</th><th style={{ textAlign: "right" }}>Custos</th><th style={{ textAlign: "right" }}>Saldo</th></tr>
            </thead>
            <tbody>
              {data.next_months.map(m => (
                <tr key={m.month}>
                  <td>{monthBR(m.month)}</td>
                  <td style={{ textAlign: "right", color: "var(--verde-2)", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>{brl(m.recebido)}</td>
                  <td style={{ textAlign: "right", color: "var(--gold)", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>{brl(m.projecao_receber)}</td>
                  <td style={{ textAlign: "right", color: "#c47a7a", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>{brl(m.custos)}</td>
                  <td style={{ textAlign: "right", fontWeight: 600, color: m.saldo_estimado >= 0 ? "var(--verde-2)" : "#c47a7a" }}>{brl(m.saldo_estimado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "obras" && (
        <div className="tbl-wrap" data-testid="obras-table">
          <table className="tbl">
            <thead>
              <tr>
                <th>Obra</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Contratado</th>
                <th style={{ textAlign: "right" }}>Recebido</th>
                <th style={{ textAlign: "right" }}>A receber</th>
                <th style={{ textAlign: "right" }}>Custos</th>
                <th style={{ textAlign: "right" }}>Margem</th>
                <th style={{ textAlign: "center" }}>%</th>
              </tr>
            </thead>
            <tbody>
              {data.obras.map(o => (
                <tr key={o.obra_id} onClick={() => navigate(`/obras/${o.obra_id}`)} style={{ cursor: "pointer" }} data-testid={`obra-row-${o.obra_id}`}>
                  <td style={{ fontWeight: 500 }}>
                    <div style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: ".15em", marginBottom: 2 }}>{o.obra_codigo}</div>
                    {o.obra_nome}
                  </td>
                  <td><span style={{ fontSize: 10, padding: "2px 8px", border: "1px solid var(--line)", color: o.status === "concluida" ? "var(--verde-2)" : "var(--gold)", textTransform: "uppercase", letterSpacing: ".15em" }}>{o.status}</span></td>
                  <td style={{ textAlign: "right", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>{brl(o.valor_total)}</td>
                  <td style={{ textAlign: "right", color: "var(--verde-2)", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>{brl(o.recebido)}</td>
                  <td style={{ textAlign: "right", color: "var(--gold)", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>{brl(o.a_receber_total)}</td>
                  <td style={{ textAlign: "right", color: "#c47a7a", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>{brl(o.custos)}</td>
                  <td style={{ textAlign: "right", color: margemColor(o.margem_pct), fontFamily: "'Fraunces',serif", fontStyle: "italic", fontWeight: 600 }}>{brl(o.margem)}</td>
                  <td style={{ textAlign: "center", color: margemColor(o.margem_pct), fontWeight: 600 }}>{o.margem_pct}%</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "1px solid var(--gold-3)" }}>
                <td colSpan={2} style={{ paddingTop: 14, fontSize: 11, letterSpacing: ".2em", textTransform: "uppercase", color: "var(--gold)" }}>Total ({data.total_obras} obras)</td>
                <td style={{ textAlign: "right", paddingTop: 14, fontFamily: "'Fraunces',serif", fontStyle: "italic", color: "var(--gold)" }}>{brl(data.total_valor_contratado)}</td>
                <td style={{ textAlign: "right", paddingTop: 14, fontFamily: "'Fraunces',serif", fontStyle: "italic", color: "var(--verde-2)" }}>{brl(data.total_recebido)}</td>
                <td style={{ textAlign: "right", paddingTop: 14, fontFamily: "'Fraunces',serif", fontStyle: "italic", color: "var(--gold)" }}>{brl(data.total_a_receber_contratual)}</td>
                <td style={{ textAlign: "right", paddingTop: 14, fontFamily: "'Fraunces',serif", fontStyle: "italic", color: "#c47a7a" }}>{brl(data.total_custos)}</td>
                <td style={{ textAlign: "right", paddingTop: 14, fontFamily: "'Fraunces',serif", fontStyle: "italic", color: margemColor(data.margem_pct), fontWeight: 600 }}>{brl(data.margem_consolidada)}</td>
                <td style={{ textAlign: "center", paddingTop: 14, color: margemColor(data.margem_pct), fontWeight: 600 }}>{data.margem_pct}%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {tab === "categorias" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }} data-testid="categorias-section">
          <div className="panel" style={{ padding: 22 }}>
            <div className="eyebrow" style={{ marginBottom: 14 }}><ArrowDownRight size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "var(--verde-2)" }} />Receitas por categoria</div>
            {data.receitas_por_categoria.length === 0 ? (
              <p style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic" }}>Nenhuma receita ainda.</p>
            ) : data.receitas_por_categoria.map(c => {
              const pct = (c.valor / data.total_recebido) * 100;
              return (
                <div key={c.categoria} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: "var(--ink-2)", textTransform: "capitalize" }}>{c.categoria}</span>
                    <span style={{ color: "var(--verde-2)", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>{brl(c.valor)}</span>
                  </div>
                  <div style={{ height: 4, background: "var(--bg-3)" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: "var(--verde-2)" }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="panel" style={{ padding: 22 }}>
            <div className="eyebrow" style={{ marginBottom: 14 }}><ArrowUpRight size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "#c47a7a" }} />Custos por categoria</div>
            {data.custos_por_categoria.length === 0 ? (
              <p style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic" }}>Nenhum custo lançado.</p>
            ) : data.custos_por_categoria.map(c => {
              const pct = Math.abs(c.valor / data.total_custos) * 100;
              const isEconomia = c.valor < 0;
              return (
                <div key={c.categoria} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: "var(--ink-2)", textTransform: "capitalize" }}>
                      {c.categoria.replace(/_/g, " ")}
                      {isEconomia && <span style={{ marginLeft: 6, fontSize: 9, color: "var(--verde-2)", letterSpacing: ".15em" }}>♻ ECONOMIA</span>}
                    </span>
                    <span style={{ color: isEconomia ? "var(--verde-2)" : "#c47a7a", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>{brl(c.valor)}</span>
                  </div>
                  <div style={{ height: 4, background: "var(--bg-3)" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: isEconomia ? "var(--verde-2)" : "#c47a7a" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "pendentes" && (
        <div data-testid="pendentes-section">
          {data.parcelas_pendentes.length === 0 ? (
            <div className="empty" style={{ padding: 30 }}>
              <h3>Nenhuma parcela pendente futura</h3>
              <p style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 8 }}>
                Para alimentar a projeção, lance receitas em cada obra com <strong>data futura</strong> e <strong>pago = false</strong> em <strong>Obra → Financeiro</strong>.
              </p>
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Obra</th>
                    <th>Categoria</th>
                    <th>Descrição</th>
                    <th style={{ textAlign: "center" }}>Vencimento</th>
                    <th style={{ textAlign: "center" }}>Em</th>
                    <th style={{ textAlign: "right" }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {data.parcelas_pendentes.map(p => (
                    <tr key={p.lancamento_id} onClick={() => navigate(`/obras/${p.obra_id}`)} style={{ cursor: "pointer" }} data-testid={`pendente-${p.lancamento_id}`}>
                      <td>
                        <div style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: ".15em", marginBottom: 2 }}>{p.obra_codigo}</div>
                        <div style={{ fontWeight: 500 }}>{p.obra_nome}</div>
                      </td>
                      <td style={{ color: "var(--ink-2)", fontSize: 12, textTransform: "capitalize" }}>{p.categoria}</td>
                      <td style={{ color: "var(--ink-2)", fontSize: 12 }}>{p.descricao || "—"}</td>
                      <td style={{ textAlign: "center" }}>{p.data_vencimento}</td>
                      <td style={{ textAlign: "center", fontSize: 11, color: p.atrasado ? "#c47a7a" : p.dias_ate_vencimento <= 7 ? "var(--gold)" : "var(--ink-2)" }}>
                        {p.atrasado ? `${Math.abs(p.dias_ate_vencimento)}d ATRASADO` : `${p.dias_ate_vencimento}d`}
                      </td>
                      <td style={{ textAlign: "right", fontFamily: "'Fraunces',serif", fontStyle: "italic", color: "var(--gold)" }}>{brl(p.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
